import type { UserOutput } from "@orsane/schema/users";
import { Link } from "@tanstack/react-router";
import { ChevronsUpDown, Cookie, LogOut, Search, Settings, Users } from "lucide-react";
import { memo } from "react";
import { toast } from "sonner";
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuPortal,
    DropdownMenuSeparator,
    DropdownMenuSub,
    DropdownMenuSubContent,
    DropdownMenuSubTrigger,
    DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
    SidebarGroup,
    SidebarGroupContent,
    SidebarHeader,
    SidebarMenu,
    SidebarMenuButton,
    SidebarMenuItem,
    useSidebar,
} from "@/components/ui/sidebar";
import { authClient } from "@/lib/auth";
import { openCookieSettings } from "@/lib/cookieConsent";
import { useSessionQuery } from "@/queries/queries";
import { useSubscription } from "@/queries/subscription-queries";
import { useMemberships } from "@/queries/user-queries";
import { CommandMenu, useCommandMenuStore } from "../CommandMenu";
import { useOrganizationSwitchStore } from "../OrganizationSwitchOverlay";
import { queryClient } from "../QueryProvider";
import { UserAvatar } from "../UserAvatar";
import { Badge } from "../ui/badge";
import { Button } from "../ui/button";
import { Logo } from "./Logo";

export const AppSidebarHeader = memo(() => {
    const { isMobile } = useSidebar();
    const { setOpen } = useCommandMenuStore();
    const startOrganizationSwitch = useOrganizationSwitchStore((state) => state.start);
    const stopOrganizationSwitch = useOrganizationSwitchStore((state) => state.stop);
    const { data: session } = useSessionQuery();
    const { data: activeOrganization } = authClient.useActiveOrganization();
    const { data: memberships } = useMemberships();
    const { data: subscriptionData } = useSubscription();
    const currentUser = session?.user as UserOutput;

    if (!currentUser) {
        return null;
    }

    const handleLogout = async () => {
        await authClient.signOut({
            fetchOptions: {
                onSuccess: () => {
                    window.location.href = "/login";
                },
            },
        });
    };

    const handleActivateOrganization = (organizationId: string, organizationName: string) => async () => {
        // Show the overlay before anything else: invalidating the queries below
        // refetches with the new organization, which would otherwise flash the
        // previous organization's tasks until the reload kicks in.
        startOrganizationSwitch(organizationName);
        const minimumOverlayDuration = new Promise((resolve) => setTimeout(resolve, 2000));

        const { error } = await authClient.organization.setActive({
            organizationId: organizationId,
        });
        if (error) {
            stopOrganizationSwitch();
            toast.error(error.message ?? "Failed to switch team");
            return;
        }

        await queryClient.invalidateQueries();
        await minimumOverlayDuration;
        window.location.href = "/inbox";
    };

    const hasActiveSubscription = !!subscriptionData?.subscription?.isActive;

    return (
        <SidebarHeader className="pr-0">
            <SidebarMenu>
                <SidebarMenuItem>
                    <DropdownMenu>
                        <DropdownMenuTrigger
                            render={
                                <SidebarMenuButton className="py-2 h-auto">
                                    <div className="flex size-9">
                                        <UserAvatar user={currentUser} size="lg" />
                                    </div>
                                    <div className="flex-1 text-left leading-3 flex flex-col gap-0.5">
                                        <div className="pt-0.5">
                                            <Logo className="w-[54px]! block" />
                                        </div>
                                        <div className="truncate text-sm text-zinc-600 leading-4">
                                            {currentUser.email}
                                        </div>
                                    </div>
                                    <ChevronsUpDown className="ml-auto" />
                                </SidebarMenuButton>
                            }
                        ></DropdownMenuTrigger>
                        <DropdownMenuContent
                            className="w-[--radix-dropdown-menu-trigger-width] rounded-lg min-w-[200px]"
                            align="start"
                            side={isMobile ? "bottom" : "right"}
                            sideOffset={4}
                        >
                            <div className="flex flex-col gap-2 p-2">
                                <div className="flex gap-2 items-center justify-between">
                                    <div className=" font-medium">
                                        {activeOrganization?.name}
                                        <div className="text-sm text-orsane-muted">{session?.user.name}</div>
                                    </div>
                                    {hasActiveSubscription && (
                                        <Badge variant="default" className="text-sm bg-blue-500 py-0.5 px-1 rounded-sm">
                                            Pro
                                        </Badge>
                                    )}
                                </div>
                            </div>
                            <DropdownMenuSeparator />
                            <DropdownMenuItem
                                render={
                                    <Link
                                        to="."
                                        search={{
                                            settings: "profile",
                                        }}
                                    >
                                        <Settings></Settings>
                                        <span>Settings</span>
                                    </Link>
                                }
                            ></DropdownMenuItem>
                            {!!memberships && memberships.length > 1 && (
                                <DropdownMenuSub>
                                    <DropdownMenuSubTrigger className="gap-2  [&_svg:not([class*='text-'])]:text-muted-foreground [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4">
                                        <Users />
                                        Switch team
                                    </DropdownMenuSubTrigger>
                                    <DropdownMenuPortal>
                                        <DropdownMenuSubContent>
                                            {memberships.map((membership) => (
                                                <DropdownMenuItem
                                                    disabled={membership.organizationId === activeOrganization?.id}
                                                    onClick={handleActivateOrganization(
                                                        membership.organizationId,
                                                        membership.organizationName
                                                    )}
                                                    key={membership.organizationId}
                                                >
                                                    {membership.organizationName}
                                                </DropdownMenuItem>
                                            ))}
                                        </DropdownMenuSubContent>
                                    </DropdownMenuPortal>
                                </DropdownMenuSub>
                            )}
                            <DropdownMenuItem data-cc="show-preferencesModal" onClick={() => openCookieSettings()}>
                                <Cookie />
                                <span>Cookie settings</span>
                            </DropdownMenuItem>
                            <DropdownMenuSeparator />
                            <DropdownMenuItem onClick={handleLogout}>
                                <LogOut></LogOut>
                                <span>Logout</span>
                            </DropdownMenuItem>
                        </DropdownMenuContent>
                    </DropdownMenu>
                </SidebarMenuItem>
            </SidebarMenu>

            <SidebarGroup className="py-0 mb-2 p-0">
                <SidebarGroupContent className="relative">
                    <Button
                        onClick={() => setOpen(true)}
                        className="bg-neutral-50 hover:bg-zinc-50 text-left flex justify-start gap-2 py-1.5 has-[>svg]:px-2 w-full h-8.5 text-zinc-400 hover:text-zinc-500 shadow-md/2 border border-zinc-300/70 cursor-pointer hover:border-zinc-300/90 text-base"
                    >
                        <Search className="w-4 h-4" />
                        <div>Search...</div>
                        <div className="text-muted-foreground/70 ms-auto -me-1 inline-flex h-6 max-h-full items-center rounded border px-1 font-[inherit] text-[0.625rem] font-medium">
                            ⌘K
                        </div>
                    </Button>
                    <CommandMenu />
                </SidebarGroupContent>
            </SidebarGroup>
        </SidebarHeader>
    );
});
AppSidebarHeader.displayName = "AppSidebarHeader";
